import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { detectDevice } from '../utils/deviceDetection';
import { formatMoney } from '../utils/money';
import { webAppVenueUrl } from '../utils/webApp';
import { imageVariant } from '../utils/images';
import { fetchVenueByCode, isValidVenueCode, venueCodeFromSlug, VenueNotFoundError } from '../api/venueByCode';
import AmenityIcon, { AMENITIES } from '../components/venue-link/AmenityIcon';
import SocialLinksRow, { socialLinkItems } from '../components/venue-link/SocialLinksRow';
import './VenueLink.css';

const PUBLIC_BASE = 'https://tenkusports.com';

const amenityKey = (amenity) =>
  `venueLink.amenity${amenity.toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`;

const spaceLabel = (space, sportNames) => {
  const sports = (space.sportTypeIds || []).map((id) => sportNames.get(id)).filter(Boolean);
  if (space.format && sports.length === 1) return `${sports[0]} ${space.format}`;
  return [sports.join(', '), space.format].filter(Boolean).join(' ');
};

const groupSpaces = (spaces, sportNames, fallback, language) => {
  const groups = new Map();
  spaces.forEach((space) => {
    const label = spaceLabel(space, sportNames) || fallback;
    const group = groups.get(label) || { label, count: 0, indoor: 0, priceFromCents: null };
    group.count += 1;
    if (space.indoor) group.indoor += 1;
    if (space.priceFromCents != null && (group.priceFromCents == null || space.priceFromCents < group.priceFromCents)) {
      group.priceFromCents = space.priceFromCents;
    }
    groups.set(label, group);
  });
  const collator = new Intl.Collator(language, { numeric: true, sensitivity: 'base' });
  return [...groups.values()].sort((a, b) => collator.compare(a.label, b.label));
};

const digitsOnly = (phone) => (phone || '').replace(/\D/g, '');

const VenueLink = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const code = venueCodeFromSlug(slug);
  const validCode = isValidVenueCode(code);
  const [state, setState] = useState(() => ({ status: validCode ? 'loading' : 'notFound', venue: null }));

  const device = detectDevice();
  const isIOS = device === 'ios';
  const isMobile = isIOS || device === 'android';
  const linkUrl = `${PUBLIC_BASE}/l/${encodeURIComponent(slug || '')}`;
  const deepLink = `sportsnest://venue?slug=${encodeURIComponent(slug || '')}`;

  const load = useCallback(() => {
    if (!validCode) {
      setState({ status: 'notFound', venue: null });
      return;
    }
    setState({ status: 'loading', venue: null });
    fetchVenueByCode(code, i18n.language)
      .then((venue) => setState({ status: 'ready', venue }))
      .catch((err) => setState({ status: err instanceof VenueNotFoundError ? 'notFound' : 'error', venue: null }));
  }, [code, validCode, i18n.language]);

  useEffect(() => {
    load();
  }, [load]);

  const [ctaVisible, setCtaVisible] = useState(true);
  const ctaObserver = useRef(null);
  const ctaRef = useCallback((node) => {
    if (ctaObserver.current) ctaObserver.current.disconnect();
    ctaObserver.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    ctaObserver.current = new IntersectionObserver(([entry]) => setCtaVisible(entry.isIntersecting));
    ctaObserver.current.observe(node);
  }, []);

  useEffect(() => {
    if (window.fbq) window.fbq('trackCustom', 'ViewVenueLinkPage');
  }, []);

  const trackStoreClick = (platform) => {
    if (window.fbq) {
      window.fbq('track', 'Lead', { content_name: 'app_download', platform, source: 'venue_link' });
    }
  };

  const trackWebClick = () => {
    if (window.fbq) {
      window.fbq('trackCustom', 'VenueWebOpen', { source: 'venue_link' });
    }
  };

  const trackContactClick = (channel) => {
    if (window.fbq) {
      window.fbq('trackCustom', 'VenueContact', { channel, source: 'venue_link' });
    }
  };

  const playStoreLink = () => {
    const base = t('downloads.googlePlayLink');
    const referrer = encodeURIComponent(`venue_link=${slug || ''}`);
    return `${base}${base.includes('?') ? '&' : '?'}referrer=${referrer}`;
  };

  const handleAppStoreClick = () => {
    trackStoreClick('ios');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(linkUrl).catch(() => {});
    }
  };

  const storeProps = isIOS
    ? { href: t('downloads.appStoreLink'), onClick: handleAppStoreClick }
    : { href: playStoreLink(), onClick: () => trackStoreClick('android') };

  if (state.status === 'loading') {
    return (
      <div className="vl-page vl-page--center">
        <div className="vl-spinner" />
      </div>
    );
  }

  if (state.status !== 'ready') {
    const notFound = state.status === 'notFound';
    return (
      <div className="vl-page vl-page--center">
        <div className="vl-empty">
          <img src="/logo192.png" alt="Tenku" className="vl-empty__logo" />
          <h1 className="vl-empty__title">
            {notFound ? t('venueLink.notFoundTitle') : t('venueLink.errorTitle')}
          </h1>
          <p className="vl-empty__text">
            {notFound ? t('venueLink.notFoundText') : t('venueLink.errorText')}
          </p>
          {notFound ? (
            <a href="/" className="vl-btn vl-btn--ghost">{t('venueLink.goHome')}</a>
          ) : (
            <button type="button" onClick={load} className="vl-btn vl-btn--ghost">{t('venueLink.retry')}</button>
          )}
        </div>
      </div>
    );
  }

  const venue = state.venue;
  const spaces = venue.spaces || [];
  const amenities = [
    ...AMENITIES.filter((a) => (venue.amenities || []).includes(a)).map((a) => ({ key: a, icon: a, label: t(amenityKey(a)) })),
    ...(venue.otherAmenities || []).map((o) => ({ key: `other:${o}`, icon: null, label: o })),
  ];
  const socialItems = socialLinkItems(venue.socialLinks);
  const logo = imageVariant(venue.logoUrl, 'sq192');
  const initial = (venue.name || '').trim().charAt(0).toUpperCase();
  const sportNames = new Map((venue.sports || []).map((s) => [s.id, s.name]));
  const spaceGroups = groupSpaces(spaces, sportNames, t('venueLink.courts'), i18n.language);
  const acceptsBookings = venue.acceptsAppBookings;
  const phoneDigits = digitsOnly(venue.contactPhone);
  const hasWhatsapp = !acceptsBookings && phoneDigits.length > 0 && Boolean(venue.contactPhoneWhatsapp);
  const hasCall = !acceptsBookings && phoneDigits.length > 0;
  const contactFirst = hasWhatsapp || hasCall;
  const whatsappUrl = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(t('venueLink.whatsappMessage'))}`;

  const eventSpaces = venue.eventSpaces || [];
  const eventTypes = venue.eventTypes || [];
  const showEvents = eventSpaces.length > 0 || eventTypes.length > 0;
  const quoteChannel = phoneDigits.length === 0 ? null : venue.contactPhoneWhatsapp ? 'whatsapp' : 'call';
  const quoteUrl = (space) => {
    if (quoteChannel === 'call') return `tel:+${phoneDigits}`;
    const message = space
      ? t('venueLink.quoteEventSpaceMessage', { space: space.name, venue: venue.name })
      : t('venueLink.quoteEventMessage', { venue: venue.name });
    return `https://wa.me/${phoneDigits}?text=${encodeURIComponent(message)}`;
  };
  const quoteButton = (space, className) => (
    <a
      href={quoteUrl(space)}
      target={quoteChannel === 'whatsapp' ? '_blank' : undefined}
      rel={quoteChannel === 'whatsapp' ? 'noopener noreferrer' : undefined}
      onClick={() => trackContactClick(`event_${quoteChannel}`)}
      className={className}
    >
      {t('venueLink.quoteEvent')}
    </a>
  );

  const iosHint = isIOS ? <p className="vl-cta__note">{t('venueLink.iosPasteHint')}</p> : null;

  const webOption = (
    <div className="vl-web">
      <span className="vl-web__or">{t('venueLink.webOr')}</span>
      <a href={webAppVenueUrl(slug)} onClick={trackWebClick} className="vl-btn vl-btn--ghost">
        {acceptsBookings ? t('venueLink.webBook') : t('venueLink.webView')}
      </a>
      <p className="vl-cta__note">{t('venueLink.webHint')}</p>
    </div>
  );

  const haveAppLink = isMobile ? (
    <button type="button" onClick={() => { window.location.href = deepLink; }} className="vl-link">
      {t('venueLink.haveApp')}
    </button>
  ) : null;

  const qrCard = (title) => (
    <div className="vl-qr">
      <div className="vl-qr__code">
        <QRCodeSVG value={linkUrl} size={176} level="H" includeMargin={false} />
      </div>
      <div>
        <p className="vl-qr__title">{title}</p>
        <p className="vl-qr__text">{t('venueLink.desktopHint')}</p>
      </div>
    </div>
  );

  const contactActions = (
    <div className="vl-actions">
      {hasWhatsapp && (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackContactClick('whatsapp')}
          className="vl-btn vl-btn--whatsapp"
        >
          {t('venueLink.whatsapp')}
        </a>
      )}
      {hasCall && (
        <a
          href={`tel:+${phoneDigits}`}
          onClick={() => trackContactClick('call')}
          className={hasWhatsapp ? 'vl-btn vl-btn--ghost' : 'vl-btn vl-btn--primary'}
        >
          {t('venueLink.call')}
        </a>
      )}
    </div>
  );

  const socialRow = socialItems.length > 0 ? (
    <SocialLinksRow
      items={socialItems}
      websiteLabel={t('venueLink.website')}
      onClick={(kind) => trackContactClick(kind)}
    />
  ) : null;

  const primaryCta = contactFirst ? (
    <section ref={ctaRef} className="vl-section vl-cta">
      {contactActions}
      {socialRow}
      {isMobile ? (
        <>
          <a {...storeProps} className="vl-link">{t('venueLink.openInApp')}</a>
          {iosHint}
        </>
      ) : (
        <div className="vl-cta__secondary">{qrCard(t('venueLink.openInApp'))}</div>
      )}
      {webOption}
    </section>
  ) : (
    <section ref={ctaRef} className="vl-section vl-cta">
      {acceptsBookings && <p className="vl-cta__value">{t('venueLink.valueBook')}</p>}
      {isMobile ? (
        <>
          <a {...storeProps} className="vl-btn vl-btn--primary">
            {acceptsBookings ? t('venueLink.ctaBook') : t('venueLink.ctaView')}
          </a>
          {iosHint}
          {haveAppLink}
        </>
      ) : (
        qrCard(acceptsBookings ? t('venueLink.ctaBook') : t('venueLink.ctaView'))
      )}
      {socialRow}
      {webOption}
    </section>
  );

  return (
    <div className={isMobile ? 'vl-page vl-page--sticky' : 'vl-page'}>
      <div className="vl-shell">
        <header className="vl-topbar">
          <a href="/" className="vl-topbar__brand">
            <img src="/logo192.png" alt="" />
            Tenku
          </a>
        </header>

        <section className="vl-section vl-header">
          <div className="vl-avatar">
            {logo ? <img src={logo} alt="" /> : initial}
          </div>
          <div>
            <h1 className="vl-title">{venue.name}</h1>
            {venue.address && <p className="vl-address">{venue.address}</p>}
          </div>
        </section>

        {primaryCta}

        {spaces.length > 0 && (
          <section className="vl-section">
            <h2 className="vl-section__title">{t('venueLink.courts')}</h2>
            <ul className="vl-courts">
              {spaceGroups.map((group) => {
                const details = group.indoor === group.count
                  ? t('venueLink.courtCountIndoor', { count: group.count })
                  : [
                      t('venueLink.courtCount', { count: group.count }),
                      group.indoor > 0 ? t('venueLink.indoorCount', { count: group.indoor }) : null,
                    ].filter(Boolean).join(', ');
                return (
                  <li key={group.label} className="vl-court">
                    <div>
                      <p className="vl-court__name">{group.label}</p>
                      <p className="vl-court__details">{details}</p>
                    </div>
                    {group.priceFromCents != null && (
                      <p className="vl-court__price">
                        {t('venueLink.priceFrom', {
                          price: formatMoney(group.priceFromCents / 100, venue.currency, venue.countryCode),
                        })}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {showEvents && (
          <section className="vl-section">
            <h2 className="vl-section__title">{t('venueLink.eventSpaces')}</h2>
            {eventTypes.length > 0 && (
              <div className="vl-events__types">
                <p className="vl-events__subtitle">{t('venueLink.eventsWeHost')}</p>
                <div className="vl-chips">
                  {eventTypes.map((name) => (
                    <span key={name} className="vl-chip">{name}</span>
                  ))}
                </div>
              </div>
            )}
            {eventSpaces.length > 0 && (
              <ul className="vl-events">
                {eventSpaces.map((space) => {
                  const photo = imageVariant((space.photos || [])[0], 'fit1280');
                  const includes = (space.includes || []).map((i) => i.trim()).filter(Boolean);
                  return (
                    <li key={space.id} className="vl-event">
                      {photo && <img src={photo} alt="" loading="lazy" className="vl-event__photo" />}
                      <div className="vl-event__body">
                        <p className="vl-court__name">{space.name}</p>
                        {space.capacity != null && (
                          <p className="vl-court__details">{t('venueLink.upToPeople', { count: space.capacity })}</p>
                        )}
                        {includes.length > 0 && (
                          <p className="vl-event__includes">{t('venueLink.includes', { items: includes.join(', ') })}</p>
                        )}
                        {quoteChannel && quoteButton(space, 'vl-btn vl-btn--ghost')}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {quoteChannel && (
              <div className="vl-events__cta">
                {quoteButton(null, quoteChannel === 'whatsapp' ? 'vl-btn vl-btn--whatsapp' : 'vl-btn vl-btn--primary')}
              </div>
            )}
          </section>
        )}

        {amenities.length > 0 && (
          <section className="vl-section">
            <h2 className="vl-section__title">{t('venueLink.amenities')}</h2>
            <div className="vl-chips">
              {amenities.map((a) => (
                <span key={a.key} className="vl-chip vl-chip--icon">
                  <AmenityIcon amenity={a.icon} className="vl-chip__icon" />
                  {a.label}
                </span>
              ))}
            </div>
          </section>
        )}
      </div>

      {isMobile && (
        <div className={ctaVisible ? 'vl-sticky vl-sticky--hidden' : 'vl-sticky'} aria-hidden={ctaVisible}>
          {contactFirst ? (
            hasWhatsapp ? (
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackContactClick('whatsapp')} className="vl-btn vl-btn--whatsapp">
                {t('venueLink.whatsapp')}
              </a>
            ) : (
              <a href={`tel:+${phoneDigits}`} onClick={() => trackContactClick('call')} className="vl-btn vl-btn--primary">
                {t('venueLink.call')}
              </a>
            )
          ) : (
            <a {...storeProps} className="vl-btn vl-btn--primary">
              {acceptsBookings ? t('venueLink.ctaBook') : t('venueLink.ctaView')}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default VenueLink;
